/**
 * oraculoAudio.js
 *
 * Helpers compartidos para el catalogo de audios del Oraculo (Cerebro 2.0).
 * Usado por StockAnalysis.jsx (stocks) y Investments.jsx (portafolios).
 *
 * El catalogo se genera con scripts/generate_oraculo_audios.js en
 * flowexpay-investments-api y se sube a S3 en:
 *   audio/oraculo/{symbol_lower}/{tier}-es.mp3       (stocks)
 *   audio/portafolio/{portfolio_id}/{tier}-es.mp3    (portafolios)
 *
 * AVAILABLE_AUDIOS debe actualizarse manualmente despues de cada generacion
 * para reflejar que (item, tier) combinaciones existen en S3.
 */

// Base URL del bucket S3 (o CloudFront frente al bucket).
// Override con VITE_AUDIO_BASE_URL si migramos a R2 o a otro CDN sin tocar codigo.
export const AUDIO_BASE = import.meta.env.VITE_AUDIO_BASE_URL || 'https://beta.flowexpay.com/audio';

export const ALL_TIERS = ['basic', 'intermediate', 'advanced', 'premium'];

/**
 * Cache-busting por simbolo. Cuando regeneramos un audio (p.ej. corregir
 * ortografia), subimos el numero aqui para que el navegador rebaje el MP3
 * nuevo en vez de servir el viejo desde su cache (Cache-Control max-age=86400).
 * Solo afecta a los simbolos listados; el resto conserva su URL limpia.
 */
export const AUDIO_VERSIONS = {
  SPCX: 2,
};

/**
 * Allowlist tier-aware. Solo combinaciones listadas aqui mostraran reproductor.
 * Catalogo completo generado May 23 2026 (76 audios).
 */
export const AVAILABLE_AUDIOS = {
  stocks: {
    AAPL: ALL_TIERS,
    MSFT: ALL_TIERS,
    NVDA: ALL_TIERS,
    TSLA: ALL_TIERS,
    PLTR: ALL_TIERS,
    HIMS: ALL_TIERS,
    DUOL: ALL_TIERS,
    VOO: ALL_TIERS,
    QQQ: ALL_TIERS,
    BND: ALL_TIERS,
    GLD: ALL_TIERS,
    SCHD: ALL_TIERS,
    SOXX: ALL_TIERS,
    ARKK: ALL_TIERS,
    VWO: ALL_TIERS,
    SPCX: ALL_TIERS,
    CAI: ALL_TIERS,
    RXRX: ALL_TIERS,
    TEM: ALL_TIERS,
    NAUT: ALL_TIERS,
    IBRX: ALL_TIERS,
    INKT: ALL_TIERS,
  },
  portfolios: {
    guardian: ALL_TIERS,
    explorer: ALL_TIERS,
    visionary: ALL_TIERS,
    income_builder: ALL_TIERS,
  },
};

/**
 * Metadata por tier para los labels del HeroAudioPlayer.
 */
export const TIER_AUDIO_META = {
  basic:        { label: 'Análisis básico',     subtitle: 'La tesis en una frase y una analogía para empezar' },
  intermediate: { label: 'Análisis intermedio', subtitle: 'Incluye el moat y el contexto macro' },
  advanced:     { label: 'Análisis avanzado',   subtitle: 'Curva de adopción, plataformas y bear case' },
  premium:      { label: 'Análisis premium',    subtitle: 'Estilo institucional — las 10 lentes del Oráculo' },
};

export const PORTFOLIO_TIER_META = {
  basic:        { label: 'Tu portafolio explicado',          subtitle: 'Filosofía y para quién es' },
  intermediate: { label: 'Tu portafolio en profundidad',     subtitle: 'Apuestas centrales y balance interno' },
  advanced:     { label: 'Tu portafolio en distintos escenarios', subtitle: 'Comportamiento macro y peor caída histórica' },
  premium:      { label: 'Tu portafolio sin filtros',        subtitle: 'Pre-mortem honesto, bear case y compañero mental' },
};

/**
 * URL del audio narrado para una accion en un tier especifico.
 * Returns null si no existe en el catalogo (allowlist).
 */
export function getStockAudioUrl(symbol, tier) {
  if (!symbol || !tier) return null;
  const sym = symbol.toUpperCase();
  const available = AVAILABLE_AUDIOS.stocks[sym] || [];
  if (!available.includes(tier)) return null;
  const v = AUDIO_VERSIONS[sym] ? `?v=${AUDIO_VERSIONS[sym]}` : '';
  return `${AUDIO_BASE}/oraculo/${sym.toLowerCase()}/${tier}-es.mp3${v}`;
}

/**
 * URL del audio narrado para un portafolio en un tier especifico.
 * portfolioId: 'guardian' | 'explorer' | 'visionary' | 'income_builder'
 */
export function getPortfolioAudioUrl(portfolioId, tier) {
  if (!portfolioId || !tier) return null;
  const id = portfolioId.toLowerCase();
  const available = AVAILABLE_AUDIOS.portfolios[id] || [];
  if (!available.includes(tier)) return null;
  return `${AUDIO_BASE}/portafolio/${id}/${tier}-es.mp3`;
}
